import React, { useState } from 'react';
import { Radio, Loader2 } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { pingAbAdapter, getAbAdapterOrder } from '../../core/api/apiService';

const DEFAULT_EBELN = '0045054083';

/**
 * AB-Modul POC: Z-REST vs. SOAP Publikationsweg vergleichen (sap/ab/README.md, Konstantins
 * Entscheidung "beide bauen und pruefen"). NUR Lesepfade (ping/readOpenPoItems) — confirmOrder
 * ist ein echter, nicht rueckholbarer SAP-Schreibvorgang und gehoert nicht in eine reine
 * Verbindungs-Vergleichsdiagnose (s. AdapterTestPanel.js fuer das Muster bei echten Schreib-Aktionen
 * inklusive Zwei-Klick-Bestaetigung, falls das hier je noetig wird).
 */
const AbAdapterComparePanel = ({ user, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [mode, setMode] = useState('zrest');
  const [ebeln, setEbeln] = useState(DEFAULT_EBELN);
  const [loadingKey, setLoadingKey] = useState(null);
  const [result, setResult] = useState(null);

  const runAction = async (key, action) => {
    setLoadingKey(key);
    setResult(null);
    try {
      const data = await action();
      setResult({ ok: true, data });
      showSuccess?.(t('admin.abAdapter.success'));
    } catch (error) {
      setResult({ ok: false, error: error.message });
      showError?.(error.message || t('admin.abAdapter.error'));
    } finally {
      setLoadingKey(null);
    }
  };

  const cardClass = `p-6 rounded-2xl border ${
    isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'
  }`;

  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm ${
    isDarkMode
      ? 'bg-white/5 border-white/20 text-white placeholder-white/40'
      : 'bg-white border-gray-300 text-gray-900'
  }`;

  const buttonClass = (busy) => `inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
    busy
      ? 'bg-gray-400 text-white cursor-not-allowed'
      : 'bg-brand text-white hover:opacity-90'
  }`;

  return (
    <div className="space-y-6">
      <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>
        <Radio className="w-4 h-4" />
        {t('admin.abAdapter.intro')}
      </div>

      {/* Mode toggle */}
      <div className={cardClass}>
        <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {t('admin.abAdapter.modeTitle')}
        </h3>
        <div className={`inline-flex rounded-lg border p-1 ${isDarkMode ? 'border-white/20' : 'border-gray-300'}`}>
          {['zrest', 'soap'].map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setResult(null); }}
              disabled={loadingKey !== null}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                mode === m
                  ? 'bg-brand text-white'
                  : isDarkMode ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {m === 'zrest' ? t('admin.abAdapter.modeZRest') : t('admin.abAdapter.modeSoap')}
            </button>
          ))}
        </div>
      </div>

      {/* Ping */}
      <div className={cardClass}>
        <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {t('admin.abAdapter.pingTitle')}
        </h3>
        <button
          className={buttonClass(loadingKey === 'ping')}
          disabled={loadingKey !== null}
          onClick={() => runAction('ping', () => pingAbAdapter(user, mode))}
        >
          {loadingKey === 'ping' && <Loader2 className="w-4 h-4 animate-spin" />}
          {t('admin.abAdapter.pingButton')}
        </button>
      </div>

      {/* Read PO */}
      <div className={cardClass}>
        <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {t('admin.abAdapter.readTitle')}
        </h3>
        <div className="flex items-center gap-2 mb-3">
          <input
            className={inputClass}
            value={ebeln}
            onChange={(e) => setEbeln(e.target.value)}
            placeholder="EBELN"
          />
        </div>
        <button
          className={buttonClass(loadingKey === 'read')}
          disabled={loadingKey !== null || !ebeln}
          onClick={() => runAction('read', () => getAbAdapterOrder(user, mode, ebeln))}
        >
          {loadingKey === 'read' && <Loader2 className="w-4 h-4 animate-spin" />}
          {t('admin.abAdapter.readButton')}
        </button>
      </div>

      {/* Result */}
      {result && (
        <div className={cardClass}>
          <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('admin.abAdapter.resultTitle')}
          </h3>
          <pre className={`text-xs overflow-auto p-3 rounded-lg max-h-96 ${
            isDarkMode ? 'bg-black/30 text-green-300' : 'bg-gray-50 text-gray-800'
          }`}>
            {JSON.stringify(result.ok ? result.data : { error: result.error }, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default AbAdapterComparePanel;
